/**
 * Enterprise-Level API Manager
 * Handles request cancellation, debouncing, and conflict detection
 */

interface PendingRequest {
  controller: AbortController;
  timestamp: number;
  endpoint: string;
}

interface CacheEntry {
  data: unknown;
  timestamp: number;
  expiry: number;
}

class APIManager {
  private pendingRequests = new Map<string, PendingRequest>();
  private cache = new Map<string, CacheEntry>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();

  // Cache TTL configs (in milliseconds)
  private cacheTTL = {
    'tenant-data': 5 * 60 * 1000, // 5 minutes - tenant info changes rarely
    services: 2 * 60 * 1000, // 2 minutes - services change occasionally
    'business-hours': 5 * 60 * 1000, // 5 minutes - hours change rarely
    'available-slots': 0, // NO CACHE - always fresh for booking conflicts
    'booking-stats': 30 * 1000, // 30 seconds - stats can be slightly stale
  };

  /**
   * Cancel all pending requests for a specific key or pattern
   * If key contains '*' or partial matches, cancels all matching requests
   */
  cancelPendingRequests(keyOrPattern: string) {
    // Exact match
    const exactPending = this.pendingRequests.get(keyOrPattern);
    if (exactPending) {
      exactPending.controller.abort();
      this.pendingRequests.delete(keyOrPattern);
      return;
    }

    // Pattern matching - cancel all requests that start with the pattern
    const keysToCancel: string[] = [];
    this.pendingRequests.forEach((pending, key) => {
      if (key.startsWith(keyOrPattern)) {
        pending.controller.abort();
        keysToCancel.push(key);
      }
    });

    // Remove cancelled requests
    keysToCancel.forEach((key) => {
      this.pendingRequests.delete(key);
    });
  }

  /**
   * Cancel all pending requests (useful for component unmount)
   */
  cancelAllRequests() {
    this.pendingRequests.forEach((pending, key) => {
      pending.controller.abort();
    });
    this.pendingRequests.clear();
  }

  /**
   * Debounced API call - only executes after user stops clicking for specified delay
   */
  debouncedCall<T>(key: string, apiCall: () => Promise<T>, delay: number = 300): Promise<T> {
    return new Promise((resolve, reject) => {
      // Cancel previous debounce timer
      const existingTimer = this.debounceTimers.get(key);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Cancel any pending request for this key
      this.cancelPendingRequests(key);

      // Set new debounce timer
      const timer = setTimeout(async () => {
        try {
          const result = await this.makeRequest(key, apiCall);
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this.debounceTimers.delete(key);
        }
      }, delay);

      this.debounceTimers.set(key, timer);
    });
  }

  /**
   * Make API request with automatic cancellation and optional caching
   */
  async makeRequest<T>(key: string, apiCall: () => Promise<T>, cacheKey?: string): Promise<T> {
    // Check cache first (if cacheKey provided and cache enabled)
    if (cacheKey) {
      const cached = this.getFromCache(cacheKey) as T | null;
      if (cached) {
        return cached;
      }
    }

    // Cancel any existing request for this key
    this.cancelPendingRequests(key);

    // Create new AbortController
    const controller = new AbortController();
    const pending: PendingRequest = {
      controller,
      timestamp: Date.now(),
      endpoint: key,
    };

    this.pendingRequests.set(key, pending);

    try {
      const result = await apiCall();

      // Cache result if cacheKey provided
      if (cacheKey && this.cacheTTL[cacheKey as keyof typeof this.cacheTTL] > 0) {
        this.setCache(cacheKey, result);
      }

      return result;
    } catch (error: unknown) {
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Request was cancelled');
        }
      }
      throw error;
    } finally {
      this.pendingRequests.delete(key);
    }
  }

  /**
   * Get data from cache if not expired
   */
  private getFromCache(key: string): unknown | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const now = Date.now();
    if (now > entry.expiry) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Set data in cache with TTL
   */
  private setCache(key: string, data: unknown) {
    const ttl = this.cacheTTL[key as keyof typeof this.cacheTTL] || 0;
    if (ttl === 0) return; // No caching for this type

    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + ttl,
    };

    this.cache.set(key, entry);
  }

  /**
   * Clear cache for specific keys (useful when data changes)
   */
  clearCache(keys: string[]) {
    keys.forEach((key) => {
      this.cache.delete(key);
    });
  }

  /**
   * Clear all cache
   */
  clearAllCache() {
    this.cache.clear();
  }

  /**
   * Enhanced fetch with AbortController support
   */
  createFetch(controller: AbortController) {
    return (url: string, options: RequestInit = {}) => {
      return fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
      });
    };
  }

  /**
   * Get cache statistics for debugging
   */
  getCacheStats() {
    const stats = {
      totalEntries: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      cacheEntries: Array.from(this.cache.entries()).map(([key, entry]) => ({
        key,
        age: Date.now() - entry.timestamp,
        timeToExpiry: entry.expiry - Date.now(),
      })),
    };

    return stats;
  }
}

// Global singleton instance
export const apiManager = new APIManager();

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    apiManager.cancelAllRequests();
  });
}
